import { getSessionId } from './session';
import { getAnalyticsEndpoint } from './hostUrl';

/**
 * Base64 encode a payload for analytics backend
 * The backend expects { data: "base64EncodedJSON" } for payload obscurity (not security)
 */
function encodePayload(payload) {
  const jsonString = JSON.stringify(payload);
  // Use browser's btoa for Base64 encoding, fallback to Buffer for SSR
  if (typeof btoa !== 'undefined') {
    return btoa(jsonString);
  } else if (typeof Buffer !== 'undefined') {
    return Buffer.from(jsonString).toString('base64');
  }
  // Fallback for environments without btoa or Buffer
  throw new Error('Base64 encoding not available');
}

/**
 * Lightweight analytics client for Gatsby marketing site
 *
 * Features:
 * - Session management (30-minute timeout)
 * - Device/browser detection
 * - Environment-aware API endpoint
 * - Fire-and-forget pattern (never throws)
 * - Base64 payload encoding for backend compatibility
 */
export class GatsbyAnalyticsClient {
  constructor(config) {
    this.config = config;
  }

  /**
   * Track generic event
   */
  trackEvent(eventType, properties) {
    try {
      const enriched = this.enrichProperties(properties || {});
      this.sendEvent(eventType, enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackEvent failed:', error);
      }
    }
  }

  /**
   * Track page view (called automatically via gatsby-browser.js)
   */
  trackPageView(page, properties) {
    try {
      const enriched = this.enrichProperties({
        ...properties,
        page,
        title:
          properties?.title ||
          (typeof document !== 'undefined' ? document.title : ''),
        referrer:
          typeof document !== 'undefined' ? document.referrer : undefined,
        ...this.captureUTMParameters(),
      });

      this.sendEvent('page.view', enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackPageView failed:', error);
      }
    }
  }

  /**
   * Track button click
   */
  trackButtonClick(buttonId, properties) {
    try {
      const enriched = this.enrichProperties({
        ...properties,
        buttonId,
      });

      this.sendEvent('cta.click.signup', enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackButtonClick failed:', error);
      }
    }
  }

  /**
   * Track navigation click
   */
  trackNavigationClick(properties) {
    try {
      const enriched = this.enrichProperties(properties);
      const eventType =
        properties.section === 'menu'
          ? 'navigation.menu.click'
          : 'navigation.footer.click';
      this.sendEvent(eventType, enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackNavigationClick failed:', error);
      }
    }
  }

  /**
   * Enrich event properties with session, device, browser, locale
   */
  enrichProperties(properties) {
    return {
      ...properties,
      sessionId: getSessionId(),
      deviceType: this.getDeviceType(),
      browser: this.getBrowser(),
      locale: typeof navigator !== 'undefined' ? navigator.language : 'en-US',
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Send event to backend via REST endpoint
   * Uses environment-aware endpoint construction (hostUrl helper)
   * Encodes payload as Base64 to match backend expectations
   */
  sendEvent(eventType, properties) {
    if (typeof window === 'undefined') {
      return;
    }

    // Build REST payload - flatten properties to root level
    const payload = {
      eventName: eventType,
      timestamp: new Date().toISOString(),
      ...properties,
      _source: this.config.source,
    };

    // Use environment-aware endpoint instead of hardcoded URL
    const endpoint = getAnalyticsEndpoint();

    try {
      // Encode payload as Base64 - backend expects { data: "base64EncodedJSON" }
      const encodedData = encodePayload(payload);

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: encodedData }),
      })
        .then((response) => {
          if (!response.ok && this.config.debug) {
            console.warn('[Analytics] REST request failed:', response.status);
          }
          return response.json();
        })
        .then((data) => {
          if (this.config.debug && data.success) {
            console.log('[Analytics] Event tracked:', data.eventId);
          }
        })
        .catch((error) => {
          if (this.config.debug) {
            console.error('[Analytics] Network error:', error);
          }
        });
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] Encoding error:', error);
      }
    }
  }

  /**
   * Capture UTM parameters from URL
   */
  captureUTMParameters() {
    if (typeof window === 'undefined') {
      return {};
    }

    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get('utm_source') || undefined,
      utmMedium: params.get('utm_medium') || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
    };
  }

  /**
   * Detect device type based on screen width
   */
  getDeviceType() {
    if (typeof window === 'undefined') {
      return 'desktop';
    }

    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Detect browser from user agent
   */
  getBrowser() {
    if (typeof navigator === 'undefined') {
      return 'unknown';
    }

    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'firefox';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari';
    if (ua.includes('Edg')) return 'edge';
    return 'other';
  }

  /**
   * Get current session ID (for passing to app via URL)
   */
  getSessionId() {
    return getSessionId();
  }
}
